import React, { useState, useContext } from "react";
import { Link as RouterLink } from "react-router-dom";
import {
  Button,
  CssBaseline,
  TextField,
  Link,
  Box,
  Backdrop,
  CircularProgress,
  Grid,
  Typography,
  InputAdornment,
  IconButton,
  Container,
  Paper,
  useTheme,
  alpha,
  Divider
} from '@mui/material';
import LoadingButton from '@mui/lab/LoadingButton';
import SaveIcon from '@mui/icons-material/Save';
import EmailOutlinedIcon from '@mui/icons-material/EmailOutlined';
import VerifiedUserOutlinedIcon from '@mui/icons-material/VerifiedUserOutlined';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import { styled } from '@mui/material/styles';
import useResponsive from '../../hooks/useResponsive';
import { i18n } from "../../translate/i18n";
import Page from '../../components/Page';
import ToastError from "../../toast/error/toastError";
import api from "../../services/api";
import logoPlaceholder from '../../assets/icons/UniChatDark.svg'; 

// Styled components com adaptação para tema escuro
const RecoveryContainer = styled(Box)(({ theme }) => ({
  display: 'flex',
  minHeight: '100vh',
  background: theme.palette.mode === 'dark'
    ? `linear-gradient(135deg, ${alpha(theme.palette.background.default, 0.9)} 0%, ${alpha(theme.palette.background.paper, 0.7)} 100%)`
    : `linear-gradient(135deg, #f6f9fc 0%, #edf2f7 100%)`,
  position: 'relative',
  overflow: 'hidden',
  [theme.breakpoints.down('md')]: {
    alignItems: 'center',
    justifyContent: 'center',
  },
}));

// Elementos decorativos abstratos para o fundo
const AbstractElement = styled(Box)(({ theme, size, top, left, color }) => ({
  position: 'absolute',
  width: size,
  height: size,
  borderRadius: '38% 62% 63% 37% / 41% 44% 56% 59%', // Forma orgânica
  background: color || theme.palette.primary.main,
  opacity: theme.palette.mode === 'dark' ? 0.07 : 0.1,
  top,
  left,
  animation: 'float 15s ease-in-out infinite',
  '@keyframes float': {
    '0%': { transform: 'translate(0px, 0px) rotate(0deg)' },
    '50%': { transform: 'translate(20px, 20px) rotate(5deg)' },
    '100%': { transform: 'translate(0px, 0px) rotate(0deg)' },
  },
}));

const GridPattern = styled(Box)(({ theme }) => ({
  position: 'absolute',
  top: 0,
  right: 0,
  bottom: 0,
  width: '60%',
  opacity: theme.palette.mode === 'dark' ? 0.05 : 0.03,
  backgroundImage: `linear-gradient(${theme.palette.primary.main} 1px, transparent 1px), linear-gradient(90deg, ${theme.palette.primary.main} 1px, transparent 1px)`,
  backgroundSize: '20px 20px',
  zIndex: 0,
}));

// Container principal com efeito de vidro
const GlassRecoveryCard = styled(Paper)(({ theme }) => ({
  width: '90%',
  maxWidth: 480,
  margin: 'auto',
  display: 'flex',
  flexDirection: 'column',
  borderRadius: 16,
  overflow: 'hidden',
  boxShadow: theme.palette.mode === 'dark'
    ? '0 25px 50px -12px rgba(0, 0, 0, 0.5)'
    : '0 25px 50px -12px rgba(0, 0, 0, 0.15)',
  background: theme.palette.mode === 'dark'
    ? alpha(theme.palette.background.paper, 0.8)
    : 'rgba(255, 255, 255, 0.95)',
  backdropFilter: 'blur(20px)',
  border: theme.palette.mode === 'dark'
    ? `1px solid ${alpha(theme.palette.common.white, 0.1)}`
    : `1px solid rgba(255, 255, 255, 0.2)`,
  position: 'relative',
  zIndex: 5,
  [theme.breakpoints.down('sm')]: {
    width: 'auto',
    margin: theme.spacing(3),
  },
}));

const RecoveryCardHeader = styled(Box)(({ theme }) => ({
  background: `linear-gradient(135deg, ${theme.palette.primary.dark} 0%, ${theme.palette.primary.main} 100%)`,
  padding: theme.spacing(4, 4, 6, 4),
  position: 'relative',
  overflow: 'hidden',
}));

const DotPattern = styled(Box)(({ theme }) => ({
  position: 'absolute',
  top: 0,
  left: 0,
  width: '100%',
  height: '100%',
  backgroundImage: `radial-gradient(rgba(255, 255, 255, 0.15) 2px, transparent 2px)`,
  backgroundSize: '30px 30px',
  zIndex: 0,
}));

const RecoveryFormSection = styled(Box)(({ theme }) => ({
  padding: theme.spacing(5, 4, 4),
  marginTop: -theme.spacing(2),
}));

const FormContainer = styled(Box)(({ theme }) => ({
  backgroundColor: theme.palette.background.paper,
  borderRadius: 12,
  padding: theme.spacing(3),
  boxShadow: '0 -4px 20px rgba(0,0,0,0.05)',
}));

// Campos de formulário estilizados
const StyledTextField = styled(TextField)(({ theme }) => ({
  marginBottom: theme.spacing(3),
  '& .MuiOutlinedInput-root': {
    borderRadius: 12,
    backgroundColor: theme.palette.mode === 'dark'
      ? alpha(theme.palette.common.white, 0.05)
      : alpha(theme.palette.common.black, 0.02),
    transition: 'all 0.2s ease-out',
    height: 56,
    '&:hover': {
      backgroundColor: theme.palette.mode === 'dark'
        ? alpha(theme.palette.common.white, 0.08)
        : alpha(theme.palette.common.black, 0.04),
    },
    '&.Mui-focused': {
      backgroundColor: theme.palette.mode === 'dark'
        ? alpha(theme.palette.common.white, 0.07)
        : theme.palette.common.white,
      boxShadow: `0 0 0 2px ${alpha(theme.palette.primary.main, theme.palette.mode === 'dark' ? 0.4 : 0.25)}`,
    }
  },
  '& .MuiInputBase-input': {
    padding: '16.5px 14px',
    color: theme.palette.text.primary,
  },
  '& .MuiInputLabel-root': {
    color: theme.palette.text.secondary,
  },
  '& .MuiOutlinedInput-notchedOutline': {
    borderColor: theme.palette.mode === 'dark'
      ? alpha(theme.palette.common.white, 0.15)
      : alpha(theme.palette.common.black, 0.1),
  },
  [theme.breakpoints.down('sm')]: {
    marginBottom: theme.spacing(2.5),
  },
  '&.Mui-disabled': {
    opacity: 0.8,
    '& .MuiOutlinedInput-root': {
      backgroundColor: theme.palette.mode === 'dark'
        ? alpha(theme.palette.common.white, 0.03)
        : alpha(theme.palette.common.black, 0.03),
    }
  }
}));

// Botão estilizado
const StyledLoadingButton = styled(LoadingButton)(({ theme }) => ({
  padding: theme.spacing(1.5, 0),
  marginTop: theme.spacing(1),
  borderRadius: 12,
  height: 56,
  fontSize: 16,
  fontWeight: 600,
  backgroundImage: `linear-gradient(135deg, ${theme.palette.primary.main} 0%, ${theme.palette.primary.dark} 100%)`,
  backgroundSize: '200% 200%',
  transition: 'all 0.3s ease',
  boxShadow: theme.palette.mode === 'dark'
    ? '0 10px 25px -5px rgba(0, 0, 0, 0.3)'
    : '0 10px 25px -5px rgba(0, 0, 0, 0.15)',
  color: '#fff',
  '&:hover': {
    backgroundPosition: 'right center',
    boxShadow: theme.palette.mode === 'dark'
      ? '0 10px 25px -3px rgba(0, 0, 0, 0.4)'
      : '0 10px 25px -3px rgba(0, 0, 0, 0.2)',
    transform: 'translateY(-3px)',
  },
  '&:active': {
    transform: 'translateY(0)',
  },
  '&.Mui-disabled': {
    opacity: 0.7,
  }
}));

// Alerta de notificação
const NotificationBox = styled(Box)(({ theme, success }) => ({
  padding: theme.spacing(2),
  borderRadius: 12,
  marginBottom: theme.spacing(3),
  backgroundColor: success
    ? alpha(theme.palette.success.main, theme.palette.mode === 'dark' ? 0.15 : 0.1)
    : alpha(theme.palette.info.main, theme.palette.mode === 'dark' ? 0.15 : 0.1),
  border: `1px solid ${success
    ? alpha(theme.palette.success.main, theme.palette.mode === 'dark' ? 0.3 : 0.2)
    : alpha(theme.palette.info.main, theme.palette.mode === 'dark' ? 0.3 : 0.2)
    }`,
  color: success
    ? theme.palette.success.dark
    : theme.palette.info.dark,
}));

const Recovery = () => {
  const theme = useTheme();
  const mdUp = useResponsive('up', 'md');
  const isDarkMode = theme.palette.mode === 'dark';

  const [user, setUser] = useState({ email: "", code: "" });
  const [loading, setLoading] = useState(false);
  const [isCode, setIsCode] = useState(false);
  const [isError, setIsError] = useState(false);

  const handleLogin = async (values) => {
    if (!values.email) return;

    if (isCode && !isError) {
      try {
        setLoading(true);
        await api.post("recovery-user/", { email: values.email, code: values.code });
        setLoading(false);
        setIsError(false);
        window.location = `/recovery-user/${values.email}/${values.code}`;
      } catch (err) {
        setIsError(true);
        ToastError(err);
        setLoading(false);
      }
    } else {
      try {
        setLoading(true);
        await api.post(`/recovery/`, values);
        setLoading(false);
        setUser(pre => ({ ...pre, code: "" }));
        setIsCode(true);
        setIsError(false);
      } catch (err) {
        console.info(err);
        ToastError(err);
        setLoading(false);
      }
    }
  };

  const handleChangeInput = (e) => {
    setUser({ ...user, [e.target.name]: e.target.value });
  };

  const handlSubmit = (e) => {
    e.preventDefault();
    handleLogin(user);
  };

  return (
    <Page title={i18n.t("recovery.title")}>
      <CssBaseline />

      <Backdrop
        sx={{ color: '#fff', zIndex: (theme) => theme.zIndex.drawer + 1 }}
        open={loading}
      >
        <CircularProgress color="inherit" />
      </Backdrop>

      <RecoveryContainer>
        {/* Elementos decorativos - visíveis apenas em desktop */}
        {mdUp && (
          <>
            <AbstractElement
              size="600px"
              top="-300px"
              left="-200px"
              color={theme.palette.primary.main}
            />
            <AbstractElement
              size="500px"
              top="70%"
              left="60%"
              color={theme.palette.secondary.main}
            />
            <AbstractElement
              size="300px"
              top="10%"
              left="75%"
              color={theme.palette.primary.dark}
            />
            <GridPattern />
          </>
        )}

        <Container maxWidth="sm" sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          height: '100vh',
          position: 'relative',
          zIndex: 2
        }}>
          <GlassRecoveryCard elevation={isDarkMode ? 4 : 0}>
            <RecoveryCardHeader>
              <DotPattern />
              <Box sx={{ position: 'relative', zIndex: 2 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
                  <Box
                    component="img"
                    src={logoPlaceholder}
                    alt="Logo"
                    sx={{ height: 60 }}
                  />
                  <Typography variant="h5" fontWeight="bold" sx={{ color: 'white' }}>
                    UniChat
                  </Typography>
                </Box>

                <Typography variant="h5" fontWeight="700" sx={{
                  color: 'white',
                  mb: 1,
                }}>
                  {i18n.t("recovery.formTitle")}
                </Typography>

                <Typography variant="body2" sx={{
                  color: 'white',
                  opacity: 0.9,
                  maxWidth: 500,
                }}>
                  {i18n.t("recovery.details")}
                </Typography>
              </Box>

              {/* Decorative elements */}
              <Box sx={{
                position: 'absolute',
                right: -20,
                bottom: -20,
                width: 120,
                height: 120,
                borderRadius: '50%',
                bgcolor: 'rgba(255,255,255,0.1)'
              }} />
              <Box sx={{
                position: 'absolute',
                right: 40,
                top: -30,
                width: 80,
                height: 80,
                borderRadius: '50%',
                bgcolor: 'rgba(255,255,255,0.05)'
              }} />
            </RecoveryCardHeader>

            <RecoveryFormSection>
              <FormContainer>
                {isCode && !isError && (
                  <NotificationBox success={true}>
                    <Typography variant="body2">
                      {i18n.t("recovery.emailSend")}
                    </Typography>
                  </NotificationBox>
                )}

                <form noValidate onSubmit={handlSubmit}>
                  <StyledTextField
                    variant="outlined"
                    required
                    fullWidth
                    id="email"
                    label={i18n.t("recovery.form.email")}
                    name="email"
                    value={user.email}
                    onChange={handleChangeInput}
                    autoComplete="email"
                    autoFocus
                    disabled={isCode && !isError}
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="start">
                          <EmailOutlinedIcon sx={{ color: theme.palette.text.secondary }} />
                        </InputAdornment>
                      ),
                    }}
                  />

                  {isCode && !isError && (
                    <StyledTextField
                      variant="outlined"
                      required
                      fullWidth
                      id="code"
                      label={i18n.t("recovery.form.code")}
                      name="code"
                      value={user.code}
                      onChange={handleChangeInput}
                      autoComplete="off"
                      InputProps={{
                        startAdornment: (
                          <InputAdornment position="start">
                            <VerifiedUserOutlinedIcon sx={{ color: theme.palette.text.secondary }} />
                          </InputAdornment>
                        ),
                      }}
                    />
                  )}

                  <StyledLoadingButton
                    loading={loading}
                    type="submit"
                    fullWidth
                    loadingPosition="start"
                    variant="contained"
                    disableElevation
                  >
                    {i18n.t(`recovery.buttons.${isCode ? isError ? 'newCode' : 'next' : 'submit'}`)}
                  </StyledLoadingButton>

                  <Box sx={{ mt: 4, textAlign: 'center' }}>
                    <Link
                      component={RouterLink}
                      to="/login"
                      variant="body2"
                      underline="hover"
                      sx={{
                        color: theme.palette.text.secondary,
                        fontWeight: 500,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        gap: '4px',
                        transition: 'color 0.2s',
                        '&:hover': { color: theme.palette.primary.main }
                      }}
                    >
                      <ArrowBackIcon fontSize="small" />
                      {i18n.t("login.buttons.login")}
                    </Link>
                  </Box>
                </form>
              </FormContainer>
            </RecoveryFormSection>
          </GlassRecoveryCard>
        </Container>
      </RecoveryContainer>
    </Page>
  );
};

export default Recovery;